/**
 * Rendered-music player. Tracks are offline-rendered orchestral loops (scripts/music) with a
 * `calm` stem and an optional additive `hot` stem (percussion/ostinati/brass). Both stems are
 * decoded, started sample-synchronously and looped with sample-accurate loop points; the hot
 * stem's gain follows intensity. Tracks crossfade on change. Only the current and previous
 * track stay decoded.
 */
import { clamp } from './dsp';
import type { AudioGraph } from './graph';

export interface TrackInfo {
  bpm: number;
  calm: string;
  hot?: string;
  /** [loopStart, loopEnd] seconds, or null for one-shot cues (intro). */
  loop: [number, number] | null;
  duration: number;
}

interface Playing {
  name: string;
  out: GainNode;
  hot?: GainNode;
  sources: AudioBufferSourceNode[];
}

const BASE = `${import.meta.env.BASE_URL}audio/music/`;
const smooth = (e0: number, e1: number, x: number): number => {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
};

export class MusicPlayer {
  private manifest?: Promise<Record<string, TrackInfo>>;
  private buffers = new Map<string, Promise<AudioBuffer | null>>();
  private current?: Playing;
  private seq = 0;
  private intensity = 0;
  currentName: string | null = null;

  constructor(private readonly graph: AudioGraph) {}

  private loadManifest(): Promise<Record<string, TrackInfo>> {
    this.manifest ??= fetch(`${BASE}manifest.json`)
      .then((r) => (r.ok ? r.json() : {}))
      .catch(() => ({}));
    return this.manifest;
  }

  async names(): Promise<string[]> {
    return Object.keys(await this.loadManifest());
  }

  private buffer(file: string): Promise<AudioBuffer | null> {
    let p = this.buffers.get(file);
    if (!p) {
      p = fetch(BASE + file)
        .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
        .then((ab) => this.graph.ctx.decodeAudioData(ab))
        .catch((e) => {
          if (import.meta.env.DEV) console.warn('[audio] music load failed', file, e);
          this.buffers.delete(file);
          return null;
        });
      this.buffers.set(file, p);
    }
    return p;
  }

  /** Warm the cache for a track (fetch + decode) without playing it. */
  async prefetch(name: string): Promise<void> {
    const info = (await this.loadManifest())[name];
    if (!info) return;
    await Promise.all([this.buffer(info.calm), info.hot ? this.buffer(info.hot) : null]);
  }

  play(name: string | null, fade = 1.5): void {
    const token = ++this.seq;
    this.currentName = name;
    const requested = this.graph.now;
    if (!name) {
      this.stopCurrent(fade);
      return;
    }
    void (async () => {
      const info = (await this.loadManifest())[name];
      if (!info) {
        if (import.meta.env.DEV) console.warn(`[audio] no music track "${name}"`);
        if (token === this.seq) this.stopCurrent(fade);
        return;
      }
      const [calm, hot] = await Promise.all([this.buffer(info.calm), info.hot ? this.buffer(info.hot) : Promise.resolve(null)]);
      if (token !== this.seq || !calm) return;
      this.stopCurrent(fade);
      this.start(name, info, calm, hot, fade, requested);
      this.trimCache(info);
    })();
  }

  private start(name: string, info: TrackInfo, calm: AudioBuffer, hot: AudioBuffer | null, fade: number, requested: number): void {
    const ctx = this.graph.ctx;
    const t = ctx.currentTime + 0.03;
    const out = ctx.createGain();
    out.gain.setValueAtTime(0.0001, t);
    out.gain.exponentialRampToValueAtTime(1, t + Math.max(0.06, Math.min(fade, 0.8)));
    out.connect(this.graph.music);
    // One-shot cues stay in sync with whatever started them (e.g. the intro video) even if
    // decoding took a moment; loops start on their first downbeat.
    const offset = info.loop ? info.loop[0] : Math.max(0, t - requested);
    const mk = (buf: AudioBuffer, dest: AudioNode): AudioBufferSourceNode => {
      const s = ctx.createBufferSource();
      s.buffer = buf;
      if (info.loop) {
        s.loop = true;
        s.loopStart = info.loop[0];
        s.loopEnd = Math.min(info.loop[1], buf.duration);
      }
      s.connect(dest);
      s.start(t, offset);
      return s;
    };
    const sources = [mk(calm, out)];
    let hotGain: GainNode | undefined;
    if (hot) {
      hotGain = ctx.createGain();
      hotGain.gain.value = this.hotLevel();
      hotGain.connect(out);
      sources.push(mk(hot, hotGain));
    }
    this.current = { name, out, hot: hotGain, sources };
  }

  private stopCurrent(fade: number): void {
    const cur = this.current;
    if (!cur) return;
    this.current = undefined;
    const t = this.graph.now;
    const g = cur.out.gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(Math.max(0.0001, g.value), t);
    g.exponentialRampToValueAtTime(0.0001, t + Math.max(0.05, fade));
    for (const s of cur.sources) s.stop(t + fade + 0.1);
    setTimeout(() => cur.out.disconnect(), (fade + 0.3) * 1000);
  }

  /** Keep only the current track (and menu/intro, which are revisited) decoded. */
  private trimCache(keep: TrackInfo): void {
    const keepFiles = new Set([keep.calm, keep.hot, 'menu_calm.m4a']);
    for (const f of [...this.buffers.keys()]) if (!keepFiles.has(f)) this.buffers.delete(f);
  }

  private hotLevel(): number {
    return 0.0001 + smooth(0.22, 0.7, this.intensity);
  }

  setIntensity(v: number): void {
    this.intensity = clamp(v, 0, 1);
    this.current?.hot?.gain.setTargetAtTime(this.hotLevel(), this.graph.now, 0.8);
  }

  getIntensity(): number {
    return this.intensity;
  }
}
